import { defineStore } from 'pinia';
import { WhatsAppCallClient } from 'dashboard/api/whatsappCallBridge';
import WhatsappCallsAPI from 'dashboard/api/whatsappCalls';

export const useWhatsAppCallsStore = defineStore('whatsappCalls', {
  state: () => ({
    calls: [],
    activeClient: null,
    activeCallId: null,
    callStatus: null,
    acceptedAt: null,
    error: null,
    isMuted: false,
    pendingPermissionFor: null,
    permissionError: null,
  }),

  getters: {
    incomingCalls: s => s.calls.filter(c => c.callId !== s.activeCallId),
    hasIncomingCall: s => s.calls.some(c => c.callId !== s.activeCallId),
    activeCall: s => s.calls.find(c => c.callId === s.activeCallId) || null,
    hasActiveCall: s => Boolean(s.activeCallId),
  },

  actions: {
    addIncomingCall(payload) {
      if (!payload?.call_id) return;
      const exists = this.calls.some(c => c.callId === payload.call_id);
      if (exists) return;
      this.calls.push({
        callId: payload.call_id,
        accountId: payload.account_id,
        inboxId: payload.inbox_id,
        conversationId: payload.conversation_id,
        contactId: payload.contact_id,
        from: payload.from,
        to: payload.to,
        callerName: payload.caller_name,
        waId: payload.wa_id,
        direction: payload.direction,
        timestamp: payload.timestamp,
        receivedAt: Date.now(),
        ringtoneIgnored: false,
      });
    },

    ignoreRingtone(callId) {
      const call = this.calls.find(c => c.callId === callId);
      if (call) call.ringtoneIgnored = true;
    },

    handleCallEnded(payload) {
      const callId = payload?.call_id;
      if (!callId) return;
      if (this.activeCallId === callId) this._teardownClient('remote_ended');
      this.calls = this.calls.filter(c => c.callId !== callId);
    },

    handleCallTaken(payload) {
      const callId = payload?.call_id;
      if (!callId) return;
      if (this.activeCallId === callId) return;
      this.calls = this.calls.filter(c => c.callId !== callId);
    },

    handleOutboundInitiated(payload) {
      if (this.calls.some(c => c.callId === payload.call_id)) return;
      this.calls.push({
        callId: payload.call_id,
        conversationId: payload.conversation_id,
        contactId: payload.contact_id,
        to: payload.to,
        callerName: payload.contact_name || payload.to,
        direction: 'BUSINESS_INITIATED',
        isOutbound: true,
        receivedAt: Date.now(),
        ringtoneIgnored: true,
      });
    },

    handlePermissionReply(payload) {
      this.permissionError = null;
      if (this.pendingPermissionFor === payload.contact_id) {
        this.pendingPermissionFor = null;
      }
    },

    async startOutboundCall({ accountId, conversationId, contactId, contactName, userToken, userId }) {
      if (this.hasActiveCall) return { ok: false, error: 'already_in_call' };
      this.error = null;
      this.permissionError = null;
      this.callStatus = 'requesting_outbound';
      try {
        const { data } = await WhatsappCallsAPI.initiateCall(accountId, conversationId);
        const callId = data.call_id;
        this.calls.push({
          callId,
          conversationId,
          contactId,
          callerName: contactName,
          direction: 'BUSINESS_INITIATED',
          isOutbound: true,
          receivedAt: Date.now(),
          ringtoneIgnored: true,
        });
        this.activeCallId = callId;
        this.callStatus = 'calling';

        this.activeClient = new WhatsAppCallClient({
          callId, userToken, userId,
          onState: s => this._onOutboundState(s, contactId),
          onError: e => this._onError(e),
        });
        await this.activeClient.accept();
        return { ok: true, callId };
      } catch (err) {
        this.error = err;
        this.callStatus = null;
        this._teardownClient('outbound_init_failed');
        return { ok: false, error: err };
      }
    },

    async requestCallPermission({ accountId, conversationId, contactId, bodyText }) {
      this.pendingPermissionFor = contactId;
      try {
        await WhatsappCallsAPI.requestCallPermission(accountId, conversationId, bodyText);
        return { ok: true };
      } catch (err) {
        this.pendingPermissionFor = null;
        this.error = err;
        return { ok: false, error: err };
      }
    },

    _onOutboundState(s, contactId) {
      this.callStatus = s.status;
      if (s.status === 'connected') this.acceptedAt = s.acceptedAt || Date.now();
      if (s.status === 'ended') {
        this._teardownClient(s.reason || 'ended');
      }
      if (s.error === 'no_call_permission' || s.code === 138006) {
        this.permissionError = { contactId, message: s.message };
        this._teardownClient('no_permission');
      }
    },

    toggleMute() {
      if (!this.activeClient) return;
      this.isMuted = !this.isMuted;
      this.activeClient.setMuted(this.isMuted);
    },

    async accept({ callId, userToken, userId }) {
      const call = this.calls.find(c => c.callId === callId);
      if (!call) return;
      this.activeCallId = callId;
      this.callStatus = 'starting';
      this.error = null;

      this.activeClient = new WhatsAppCallClient({
        callId,
        userToken,
        userId,
        onState: s => this._onState(s),
        onError: e => this._onError(e),
      });

      try {
        await this.activeClient.accept();
      } catch (err) {
        this._onError({ source: 'accept', error: err });
        this._teardownClient('accept_error');
      }
    },

    async reject({ callId, userToken, userId }) {
      const call = this.calls.find(c => c.callId === callId);
      if (!call) return;
      if (this.activeCallId === callId && this.activeClient) {
        this.activeClient.reject();
        this._teardownClient('rejected');
        return;
      }
      try {
        const client = new WhatsAppCallClient({
          callId, userToken, userId,
          onState: () => {},
          onError: () => {},
        });
        await client.sendOneShotReject();
      } catch (_) { /* noop */ }
      this.calls = this.calls.filter(c => c.callId !== callId);
    },

    hangup() {
      if (!this.activeClient) return;
      this.activeClient.hangup();
      this._teardownClient('hangup');
    },

    dismiss(callId) {
      this.calls = this.calls.filter(c => c.callId !== callId);
    },

    _onState(s) {
      this.callStatus = s.status;
      if (s.status === 'connected') this.acceptedAt = s.acceptedAt || Date.now();
      if (s.status === 'ended') {
        this._teardownClient(s.reason || 'ended');
      }
    },

    _onError(e) {
      this.error = e;
      if (e?.error === 'no_call_permission' || e?.code === 138006) {
        const active = this.activeCall;
        this.permissionError = {
          contactId: active?.contactId,
          conversationId: active?.conversationId,
          message: e?.message,
        };
        this._teardownClient('no_permission');
      }
    },

    _teardownClient(_reason) {
      const callId = this.activeCallId;
      try { this.activeClient?.cleanup(); } catch (_) { /* noop */ }
      this.activeClient = null;
      this.activeCallId = null;
      this.acceptedAt = null;
      this.callStatus = null;
      this.isMuted = false;
      if (callId) this.calls = this.calls.filter(c => c.callId !== callId);
    },
  },
});
